import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState("loading");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          clear();
          return;
        }
      } catch {
        // keep polling briefly
      }
      attempts.current += 1;
      if (attempts.current < 15) {
        setTimeout(poll, 2000);
      } else {
        setState("failed");
      }
    };
    poll();
  }, [sessionId, clear]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-white px-4 font-body text-zinc-900" data-testid="payment-success-page">
      <img src="/assets/logo-black.png" alt="Tiger Clothing" className="mb-8 h-20 w-auto" />
      {state === "loading" && (
        <div className="flex flex-col items-center gap-4" data-testid="payment-loading">
          <Loader2 size={40} className="animate-spin text-tiger" />
          <p className="font-heading text-sm font-semibold uppercase tracking-widest text-zinc-600">Confirming your payment...</p>
        </div>
      )}
      {state === "paid" && (
        <div className="flex max-w-md flex-col items-center gap-4 text-center" data-testid="payment-confirmed">
          <CheckCircle2 size={56} className="text-tiger" />
          <h1 className="font-display text-4xl uppercase">Order Confirmed</h1>
          <p className="font-body text-sm text-zinc-500">
            Payment received. Your gear now goes into production — printed on demand and shipped in 2-5 working days.
          </p>
          <p className="font-body text-xs uppercase tracking-widest text-zinc-400">Session {sessionId}</p>
          <Link
            to="/gb/en/home.html"
            data-testid="success-home-link"
            className="mt-4 bg-tiger px-8 py-4 font-heading text-sm font-bold uppercase tracking-widest text-black transition-colors hover:bg-tiger-hover"
          >
            Back To The Den
          </Link>
        </div>
      )}
      {state === "failed" && (
        <div className="flex max-w-md flex-col items-center gap-4 text-center" data-testid="payment-unconfirmed">
          <XCircle size={56} className="text-zinc-400" />
          <h1 className="font-display text-4xl uppercase">Could Not Confirm</h1>
          <p className="font-body text-sm text-zinc-500">
            We couldn't confirm this payment yet. If you were charged, it will appear shortly — contact hello@tiger-clothing.co.uk with your receipt.
          </p>
          <Link to="/gb/en/home.html" data-testid="failed-home-link" className="mt-4 border-2 border-zinc-900 px-8 py-4 font-heading text-sm font-bold uppercase tracking-widest text-zinc-900 hover:border-tiger hover:text-tiger">
            Back To Shop
          </Link>
        </div>
      )}
    </div>
  );
}
